/**
 * DOM paint layer: resolved SDUI nodes → HTMLElement.
 */
import {
  applyActiveVariants,
  normalizeBlockForPaint,
} from "../preview/block.js";
import { resolveNodeForBreakpoint } from "../contract/breakpoints.js";
import { interpolate, isColorValue, parseColor, resolveColor } from "../contract/tokens.js";
import { isDictionaryKey, isLocalizedTextRef, resolveLocalizedText } from "../preview/localization.js";
import {
  applyBorderRadius,
  applyLayoutBox,
  applyDirection,
  borderRadius,
  resolveBoxSide,
  resolveLayout,
  applyBox,
  FONT_WEIGHT,
  isWidgetVisualRootPath,
} from "./layout.js";
import { materializeWidgetNode, phaseTokenToId, resolveWidgetCatalogName } from "../preview/widgets.js";

const IMAGE_ALIGN_X = { left: "left", start: "left", center: "center", right: "right", end: "right" };
const IMAGE_ALIGN_Y = { top: "top", start: "top", center: "center", bottom: "bottom", end: "bottom" };

/** AdminApp origin for Menu item / Image blobs (from viewer-config). Trailing slash. */
let _adminImageBaseUrl = "";

/** Configure where relative `/Image/...` and `/image/...` paths are fetched from. */
export function setAdminImageBaseUrl(url) {
  if (typeof url !== "string" || !url.trim()) {
    _adminImageBaseUrl = "";
    return;
  }
  _adminImageBaseUrl = url.trim().replace(/\/?$/, "/");
}

/**
 * Resolve icon/media URL for preview.
 * Relative AdminApp image paths (`/Image/...`, `/image/...`) go through the BFF proxy
 * `/api/admin-image/...`, which fetches the binary from SLON_ADMIN_API_BASE_URL
 * (AdminApp domain from settings) with the operator token.
 * Absolute http(s)/data/blob and other relative paths (e.g. /sidebar/*.svg) stay as-is.
 */
export function resolveAdminMediaUrl(url, _ctx) {
  if (typeof url !== "string" || !url) return url;
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  if (!/^\/[Ii]mage\//.test(path)) return url;
  return `/api/admin-image${path}`;
}

function relativePercent(spec, fallback) {
  if (spec?.type === "relative" && Number.isFinite(spec.value)) return `${spec.value * 100}%`;
  return fallback;
}

function gradientStops(colors, vars) {
  return (colors || []).map((color) => resolveColor(color, vars)).join(", ");
}

// DivKit paints backgrounds bottom-up, CSS paints the first layer on top.
function applyBackground(el, background, vars) {
  const layers = [];
  let baseColor = null;
  for (const item of background || []) {
    if (!item) continue;
    if (item.type === "solid" && item.color) {
      baseColor = resolveColor(item.color, vars);
      continue;
    }
    if ((item.type === "gradient" || item.type === "linear_gradient") && item.colors?.length) {
      const angle = Number.isFinite(item.angle) ? item.angle : 0;
      layers.push({ image: `linear-gradient(${90 - angle}deg, ${gradientStops(item.colors, vars)})` });
      continue;
    }
    if (item.type === "radial_gradient" && item.colors?.length) {
      const x = relativePercent(item.center_x, "50%");
      const y = relativePercent(item.center_y, "50%");
      layers.push({ image: `radial-gradient(farthest-corner at ${x} ${y}, ${gradientStops(item.colors, vars)})` });
      continue;
    }
    if (item.type === "image" && item.image_url) {
      const x = IMAGE_ALIGN_X[item.content_alignment_horizontal] || "center";
      const y = IMAGE_ALIGN_Y[item.content_alignment_vertical] || "center";
      layers.push({
        image: `url("${interpolate(item.image_url, vars)}")`,
        size: item.scale === "fit" ? "contain" : item.scale === "no_scale" ? "auto" : "cover",
        position: `${x} ${y}`,
      });
    }
  }
  if (baseColor) el.style.backgroundColor = baseColor;
  if (!layers.length) return;
  const ordered = layers.reverse();
  el.style.backgroundImage = ordered.map((layer) => layer.image).join(", ");
  el.style.backgroundSize = ordered.map((layer) => layer.size || "auto").join(", ");
  el.style.backgroundPosition = ordered.map((layer) => layer.position || "center").join(", ");
  el.style.backgroundRepeat = "no-repeat";
  el.style.overflow = "hidden";
}

function isWhiteTint(color) {
  const parsed = parseColor(color);
  return parsed === "rgb(255, 255, 255)" || parsed === "rgba(255, 255, 255, 1)";
}

function nodeLabel(node) {
  if (!node || typeof node !== "object") return "node";
  if (typeof node.widget === "string" && node.widget) {
    return node.template ? `${node.widget} · ${node.template}` : node.widget;
  }
  const text = node.text ?? node.appearance?.text;
  if (node.type === "text") return `text · ${text || ""}`;
  if (node.type === "icon" || node.type === "image" || node.type === "custom") {
    const source = iconSource(node);
    if (!source) return "icon";
    if (source.kind === "url") return `icon · ${fileName(source.url) || "url"}`;
    if (source.kind === "asset") return `icon · ${source.id ? `#${source.id}` : "asset"}`;
    if (source.kind === "glyph") return `icon · ${source.name || "glyph"}`;
    if (source.kind === "sprite") return `icon · ${source.ref || "sprite"}`;
    return "icon";
  }
  const name = node.action?.event || node.id || node.accessibility?.description;
  return name ? `container · ${name}` : "container";
}

function fileName(url) {
  if (!url) return "";
  const part = url.split("/").pop() || url;
  return part.split("?")[0];
}

const BELL = `<svg viewBox="0 0 16 16" width="100%" height="100%" aria-hidden="true"><path fill="currentColor" d="M5.848 14.142c-.14-.014-.253.116-.202.248l.02.05a2.525 2.525 0 0 0 4.688-.05c.052-.132-.06-.262-.201-.248-.63.062-1.348.103-2.153.103s-1.523-.04-2.152-.103M13.51 8.586c-.551-.47-1.078-.917-.909-1.771.284-2.43-1.252-4.642-3.109-5.382l-.031-.111a2.3 2.3 0 0 0-.343-.695 1.65 1.65 0 0 0-.513-.464A1.23 1.23 0 0 0 8 0c-.208 0-.413.055-.605.163a1.65 1.65 0 0 0-.513.464 2.3 2.3 0 0 0-.374.807c-1.856.74-3.393 2.95-3.108 5.381.168.853-.364 1.305-.917 1.776-.524.445-1.066.906-1.055 1.738 0 1.165.657 1.797 1.622 2.11 0 0 1.767.683 4.95.683s4.95-.683 4.95-.683c.966-.313 1.623-.977 1.623-2.11 0-.84-.543-1.3-1.063-1.743"/></svg>`;

const ARROW_BOTTOM = `<svg viewBox="0 0 16 16" width="100%" height="100%" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="M2.697 5.03a.9.9 0 0 1 1.273 0L8 9.06l4.03-4.03a.9.9 0 1 1 1.273 1.273L8.637 10.97a.9.9 0 0 1-1.273 0L2.697 6.303a.9.9 0 0 1 0-1.273"/></svg>`;

const ARROW_TOP = `<svg viewBox="0 0 16 16" width="100%" height="100%" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="M13.303 10.97a.9.9 0 0 1-1.273 0L8 6.94l-4.03 4.03a.9.9 0 1 1-1.273-1.273L7.363 5.03a.9.9 0 0 1 1.273 0l4.667 4.667a.9.9 0 0 1 0 1.273"/></svg>`;

const USER = `<svg viewBox="0 0 16 16" width="100%" height="100%" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M7.992 9.604c-1.775.013-7.18.05-7.179 3.855C.886 14.979 1.968 16 3.507 16h8.988c1.527 0 2.608-1.02 2.692-2.562.016-3.766-5.406-3.817-7.195-3.834M8.005 7.736a3.87 3.87 0 0 0 3.868-3.867A3.873 3.873 0 0 0 8.005 0a3.873 3.873 0 0 0-3.868 3.87 3.87 3.87 0 0 0 3.868 3.866"/></svg>`;

const ARROW_FORWARD = `<svg viewBox="0 0 16 16" width="100%" height="100%" aria-hidden="true"><path fill="currentColor" d="M5.03 13.303a.9.9 0 0 1 0-1.273L9.06 8 5.03 3.97a.9.9 0 1 1 1.273-1.273l4.667 4.667a.9.9 0 0 1 0 1.273l-4.667 4.666a.9.9 0 0 1-1.273 0"/></svg>`;

const ARROW_BACK = `<svg viewBox="0 0 16 16" width="100%" height="100%" aria-hidden="true"><path fill="currentColor" d="M10.97 13.303a.9.9 0 0 0 0-1.273L6.94 8l4.03-4.03a.9.9 0 1 0-1.273-1.273L5.03 7.364a.9.9 0 0 0 0 1.273l4.667 4.666a.9.9 0 0 0 1.273 0"/></svg>`;

const DOUBLE_ARROW = `<svg viewBox="0 0 16 16" width="100%" height="100%" aria-hidden="true"><path fill="currentColor" d="M8.184 12.623a.9.9 0 0 1 .026-1.272L11.7 8 8.21 4.648a.9.9 0 0 1 1.247-1.298l4.166 4a.9.9 0 0 1 0 1.298l-4.166 4a.9.9 0 0 1-1.273-.026m-5.833 0a.9.9 0 0 1 .026-1.272L5.867 8l-3.49-3.352a.9.9 0 0 1 1.246-1.298l4.167 4a.9.9 0 0 1 0 1.298l-4.167 4a.9.9 0 0 1-1.272-.026"/></svg>`;

const MORE_VERTICAL = `<svg viewBox="0 0 16 16" width="100%" height="100%" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" d="M8 11.153c.746 0 1.35.604 1.35 1.35v.006a1.35 1.35 0 1 1-2.7 0v-.006c0-.746.605-1.35 1.35-1.35M8 6.647c.746 0 1.35.604 1.35 1.35v.006a1.35 1.35 0 1 1-2.7 0v-.006c0-.746.605-1.35 1.35-1.35M8 2.141c.746 0 1.35.604 1.35 1.35v.006a1.35 1.35 0 1 1-2.7 0v-.006c0-.746.605-1.35 1.35-1.35"/></svg>`;

const MESSAGES = `<svg viewBox="0 0 16 16" width="100%" height="100%" aria-hidden="true"><path fill="currentColor" d="M.184 13.608c-.324.175-.189.602.161.653 1.175.095 2.858.123 4.384-.26.161-.04.33-.022.48.05a7.7 7.7 0 0 0 3.649.762c3.852-.125 7.03-3.09 7.138-6.682.135-4.12-3.555-7.435-8-7.109-3.61.277-6.546 3.015-6.789 6.38a6.44 6.44 0 0 0 .826 3.696.65.65 0 0 1 .01.654c-.442.733-1.039 1.418-1.86 1.855"/></svg>`;

const SCROLL_TOP = `<svg viewBox="0 0 16 16" width="100%" height="100%" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="M2.1 2a.9.9 0 0 1 .9-.9h9.021a.9.9 0 1 1 0 1.8H3a.9.9 0 0 1-.9-.9m4.51 5.161v6.84a.9.9 0 1 0 1.8 0V7.156l2.181 2.19a.9.9 0 0 0 1.276-1.27L8.151 4.347a.9.9 0 0 0-.638-.265H7.51c-.27 0-.513.12-.678.308L3.156 8.077A.9.9 0 1 0 4.43 9.348z"/></svg>`;

const MACOS = `<svg viewBox="0 0 16 16" width="100%" height="100%" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="M0 8c0-4.464 3.536-8 8-8s8 3.536 8 8-3.536 8-8 8-8-3.535-8-8m8.806-2.824v.132a.35.35 0 0 1-.125.274.4.4 0 0 1-.14.08.5.5 0 0 1-.174.028.38.38 0 0 1-.233-.064.2.2 0 0 1-.087-.173q0-.112.088-.175a.5.5 0 0 1 .261-.074zm.747-.434a.8.8 0 0 1 .16-.273.7.7 0 0 1 .25-.174.8.8 0 0 1 .324-.062q.16 0 .29.048.131.048.224.13a.62.62 0 0 1 .213.42h-.322a.4.4 0 0 0-.043-.124.3.3 0 0 0-.082-.1.4.4 0 0 0-.12-.066.47.47 0 0 0-.342.018.4.4 0 0 0-.142.116.6.6 0 0 0-.092.183.8.8 0 0 0-.033.24q0 .137.033.244a.5.5 0 0 0 .093.183.4.4 0 0 0 .143.114.4.4 0 0 0 .185.04q.165 0 .269-.078a.36.36 0 0 0 .135-.227h.321a.64.64 0 0 1-.225.426.7.7 0 0 1-.222.12.9.9 0 0 1-.279.041.9.9 0 0 1-.326-.06.7.7 0 0 1-.25-.171.8.8 0 0 1-.161-.272 1.1 1.1 0 0 1-.057-.36q0-.197.056-.356m-4.248-.487h-.322v1.687h.318V4.907c0-.042.017-.082.032-.119q.008-.016.013-.033a.34.34 0 0 1 .33-.23.4.4 0 0 1 .135.023.28.28 0 0 1 .167.166Q6 4.773 6 4.845v1.097h.331V4.895q0-.08.027-.149a.34.34 0 0 1 .184-.194.4.4 0 0 1 .142-.027q.159 0 .244.091a.36.36 0 0 1 .085.257v1.069h.336v-1.15a.7.7 0 0 0-.04-.228.49.49 0 0 0-.284-.288.6.6 0 0 0-.226-.04.6.6 0 0 0-.172.023.56.56 0 0 0-.27.169.5.5 0 0 0-.086.136h-.008a.45.45 0 0 0-.173-.243.5.5 0 0 0-.299-.084.6.6 0 0 0-.157.02.5.5 0 0 0-.246.157.5.5 0 0 0-.077.127h-.006zm.408 8.073c-1.475 0-2.4-1.025-2.4-2.66s.925-2.664 2.4-2.664S8.11 8.032 8.11 9.668c0 1.635-.921 2.66-2.396 2.66m2.564-6.37a.7.7 0 0 0 .317-.08.6.6 0 0 0 .124-.094.5.5 0 0 0 .091-.123h.008v.28h.321V4.786a.494.494 0 0 0-.185-.401.6.6 0 0 0-.213-.112 1 1 0 0 0-.279-.039.9.9 0 0 0-.264.04.7.7 0 0 0-.21.108.5.5 0 0 0-.142.165.45.45 0 0 0-.055.206h.315a.27.27 0 0 1 .115-.175.4.4 0 0 1 .104-.049.5.5 0 0 1 .128-.017q.17 0 .262.076t.092.222v.13l-.455.027q-.314.02-.479.145a.41.41 0 0 0-.165.347.5.5 0 0 0 .042.206.45.45 0 0 0 .117.158q.075.065.18.1.105.037.23.037m2.288 6.37c-1.124 0-1.889-.589-1.942-1.514h.732c.057.529.568.879 1.268.879.67 0 1.153-.35 1.153-.829 0-.414-.293-.664-.971-.835l-.66-.165c-.95-.235-1.383-.667-1.383-1.378 0-.875.764-1.482 1.854-1.482 1.064 0 1.806.61 1.835 1.49h-.725c-.05-.53-.482-.854-1.125-.854-.64 0-1.082.328-1.082.803 0 .375.279.596.957.768l.557.139c1.06.257 1.497.675 1.497 1.425 0 .957-.758 1.553-1.965 1.553m-6.494-2.66c0-1.232.632-2.007 1.642-2.007 1.007 0 1.643.775 1.642 2.006 0 1.229-.635 2.004-1.642 2.004-1.01 0-1.642-.775-1.642-2.004"/></svg>`;

const INLINE_ICONS = {
  notification: BELL,
  "arrow-bottom": ARROW_BOTTOM,
  "arrow-top": ARROW_TOP,
  "arrow-forward": ARROW_FORWARD,
  "arrow-back": ARROW_BACK,
  "double-arrow-forward": DOUBLE_ARROW,
  user: USER,
  "more-vertical": MORE_VERTICAL,
  messages: MESSAGES,
  "scroll-top": SCROLL_TOP,
  macos: MACOS,
  lock: `<svg viewBox="0 0 16 16" width="100%" height="100%" aria-hidden="true"><path fill="currentColor" fill-rule="evenodd" clip-rule="evenodd" d="M4.667 7V5.333a3.333 3.333 0 0 1 6.666 0V7h.667a.667.667 0 0 1 .667.667v5.333a.667.667 0 0 1-.667.667H4a.667.667 0 0 1-.667-.667V7.667A.667.667 0 0 1 4 7h.667Zm1.333 0h4V5.333a2 2 0 1 0-4 0V7Z"/></svg>`,
};

const BRAND_COMPACT_SPRITE_INNER = `<g fill="#fff" clip-path="url(#hv-clip-brand-compact)"><path d="M14.203 8.046c.802.958 1.745 2.167 1.28 5.02l-1.259 6.716.231 5.412c.043 1.93.973 2.787 2.602 2.787l4.087.006c1.298 0 1.817-.987 2.343-2.24l3.118-7.685.316 7.333c.027 1.626.854 2.592 2.352 2.592l4.661-.003c1.325 0 1.878-.793 2.459-2.227l5.947-14.711c.644-1.523.273-3.006-1.337-3.006h-2.984c-1.113 0-1.857.386-2.295 1.444l-4.017 9.858-.368-8.795c-.06-1.869-.954-2.507-2-2.507h-3.038c-1.2 0-1.9.48-2.273 1.392l-4.033 9.913-.361-9.01c-.052-1.69-.614-2.292-1.97-2.292l-3.46.003ZM13.337 12.556c.362-1.787-.307-3.09-1.483-3.944-1.115-.748-2.495-.84-3.941-.113l-6.127 2.762c-1.522.687-2.203 2.483-1.522 4.015.68 1.531 2.47 2.215 3.993 1.531l.82-.37-1.318 7.18c-.38 2.152 1.027 3.942 2.935 4.303 2.027.383 3.975-.948 4.315-2.966l2.328-12.398Z"/></g><defs><clipPath id="hv-clip-brand-compact"><path fill="#fff" d="M0 8h43v20H0z"/></clipPath></defs>`;

const SVG_NS = "http://www.w3.org/2000/svg";
const XLINK_NS = "http://www.w3.org/1999/xlink";

export function createSpriteSheet() {
  const sheet = document.createElementNS(SVG_NS, "svg");
  sheet.setAttribute("aria-hidden", "true");
  sheet.id = "hv-sprite-sheet";
  sheet.style.position = "absolute";
  sheet.style.width = "0";
  sheet.style.height = "0";
  sheet.style.overflow = "hidden";
  const symbol = document.createElementNS(SVG_NS, "symbol");
  symbol.id = "sprite:icon:brand-compact";
  symbol.setAttribute("viewBox", "0 0 44 36");
  symbol.dataset.width = "44";
  symbol.dataset.height = "36";
  symbol.innerHTML = BRAND_COMPACT_SPRITE_INNER;
  sheet.append(symbol);
  return sheet;
}

function iconColor(node, ctx) {
  const raw = node.color ?? node.tint_color;
  return raw ? resolveColor(raw, ctx.vars) : null;
}

function iconSource(node) {
  if (node.source?.kind) return node.source;
  if (node.appearance?.source?.kind) return node.appearance.source;
  if (node.type === "image") {
    if (node.image_url) return { kind: "url", url: node.image_url };
    if (node.image_id) return { kind: "asset", id: node.image_id };
    return { kind: "asset" };
  }
  if (node.type === "custom") {
    if (node.custom_type === "svg_sprite") return { kind: "sprite", ref: node.custom_props?.ref };
    return {
      kind: "glyph",
      name: node.custom_props?.data_icon,
      view_box: node.custom_props?.view_box,
    };
  }
  return null;
}

function renderIconMaskedUrl(node, ctx, url) {
  const resolved = resolveAdminMediaUrl(url, ctx);
  const box = document.createElement("div");
  box.className = "hv-node hv-icon-box";
  const layout = applyLayoutBox(box, node, ctx, { paddings: false });
  box.style.display = "grid";
  box.style.placeItems = "center";
  box.style.flex = "0 0 auto";
  const icon = document.createElement("div");
  icon.className = "hv-node hv-masked-icon";
  const width = layout.width?.type === "fixed" ? layout.width.value : 20;
  const height = layout.height?.type === "fixed" ? layout.height.value : 20;
  icon.style.width = `${width}px`;
  icon.style.height = `${height}px`;
  icon.style.backgroundColor = iconColor(node, ctx) || resolveColor("#FFFFFFFF", ctx.vars);
  icon.style.maskImage = `url("${resolved}")`;
  icon.style.webkitMaskImage = `url("${resolved}")`;
  icon.style.maskSize = "contain";
  icon.style.webkitMaskSize = "contain";
  icon.style.maskRepeat = "no-repeat";
  icon.style.webkitMaskRepeat = "no-repeat";
  icon.style.maskPosition = "center";
  icon.style.webkitMaskPosition = "center";
  box.append(icon);
  stamp(box, node, ctx);
  return box;
}

function shouldTintUrlIcon(node, ctx) {
  if (node.tint_mode === "source_in") return true;
  return Boolean(iconColor(node, ctx));
}

function renderIconUrl(node, ctx, url) {
  if (url && shouldTintUrlIcon(node, ctx)) return renderIconMaskedUrl(node, ctx, url);
  const el = document.createElement("img");
  el.className = "hv-node hv-image";
  applyLayoutBox(el, node, ctx, { paddings: false });
  el.style.display = "block";
  el.style.objectFit = node.scale === "fill" ? "cover" : "contain";
  el.style.flex = "0 0 auto";
  applyBorderRadius(el, borderRadius(node), ctx.vars);
  el.alt = "";
  el.src = resolveAdminMediaUrl(url, ctx);
  const rawColor = node.color ?? node.tint_color;
  if (rawColor && isWhiteTint(rawColor)) {
    el.style.filter = "brightness(0) invert(1)";
  }
  stamp(el, node, ctx);
  return el;
}

function renderIconAsset(node, ctx, assetId) {
  const el = document.createElement("div");
  el.className = "hv-node hv-image hv-placeholder";
  applyLayoutBox(el, node, ctx, { paddings: false });
  el.style.display = "grid";
  el.style.placeItems = "center";
  el.style.flex = "0 0 auto";
  el.textContent = assetId ? `#${assetId}` : "icon";
  el.style.background = "#2a3140";
  el.style.color = "#d7deea";
  el.style.fontSize = "11px";
  applyBorderRadius(el, borderRadius(node), ctx.vars);
  stamp(el, node, ctx);
  return el;
}

function renderIconGlyph(node, ctx, source) {
  const el = document.createElement("span");
  el.className = "hv-node hv-custom";
  applyLayoutBox(el, node, ctx, { paddings: false });
  el.style.display = "inline-grid";
  el.style.placeItems = "center";
  el.style.color = iconColor(node, ctx) || "currentColor";
  el.style.flex = "0 0 auto";
  el.innerHTML = INLINE_ICONS[source.name] || "";
  if (!el.innerHTML) {
    el.textContent = source.name || "icon";
  }
  stamp(el, node, ctx);
  return el;
}

function renderIconSprite(node, ctx, ref) {
  const el = document.createElementNS(SVG_NS, "svg");
  el.classList.add("hv-node", "hv-custom", "hv-sprite");
  applyLayoutBox(el, node, ctx, { paddings: false });
  el.style.display = "block";
  el.style.flex = "0 0 auto";
  el.setAttribute("aria-hidden", "true");
  const color = iconColor(node, ctx);
  if (color) el.style.color = color;
  const use = document.createElementNS(SVG_NS, "use");
  use.setAttribute("href", ref);
  use.setAttributeNS(XLINK_NS, "xlink:href", ref);
  el.append(use);
  stamp(el, node, ctx);
  return el;
}

function renderIcon(node, ctx) {
  const source = iconSource(node);
  if (!source) return renderIconAsset(node, ctx, null);
  switch (source.kind) {
    case "url":
      return source.url
        ? renderIconUrl(node, ctx, source.url)
        : renderIconAsset(node, ctx, null);
    case "asset":
      return renderIconAsset(node, ctx, source.id);
    case "glyph":
      return renderIconGlyph(node, ctx, source);
    case "sprite":
      return renderIconSprite(node, ctx, source.ref || "");
    default:
      return renderIconAsset(node, ctx, null);
  }
}

function applyFont(el, font, vars = {}) {
  if (!font) return;
  if (font.size != null) {
    const resolved = resolveBoxSide(font.size, vars);
    if (resolved) el.style.fontSize = resolved;
  }
  if (font.line_height != null) {
    const resolved = resolveBoxSide(font.line_height, vars);
    if (resolved) el.style.lineHeight = resolved;
  }
  if (font.weight != null) el.style.fontWeight = String(FONT_WEIGHT[font.weight] || font.weight);
}

function applyTextWrap(el, font) {
  const mode = font?.wrap != null ? String(font.wrap).toLowerCase() : "nowrap";
  if (mode === "word" || mode === "break" || mode === "wrap") {
    el.style.whiteSpace = "normal";
    el.style.overflowWrap = "break-word";
    return;
  }
  if (mode === "pretty") {
    el.style.whiteSpace = "normal";
    el.style.textWrap = "pretty";
    el.style.overflowWrap = "break-word";
    return;
  }
  if (mode === "balance") {
    el.style.whiteSpace = "normal";
    el.style.textWrap = "balance";
    return;
  }
  el.style.whiteSpace = "nowrap";
}

function applyInteractionHover(el, node, vars) {
  const hover = node?.interaction?.hover;
  if (!hover) return;
  const baseBackground = Array.isArray(node.background) ? node.background : null;
  const baseRadius = node.border?.radius ?? node.border?.corner_radius;
  el.addEventListener("mouseenter", () => {
    if (hover.background) applyBackground(el, hover.background, vars);
    applyBorderRadius(el, hover.border?.radius, vars);
  });
  el.addEventListener("mouseleave", () => {
    if (baseBackground?.length) applyBackground(el, baseBackground, vars);
    else el.style.background = "";
    applyBorderRadius(el, baseRadius, vars);
  });
}

function renderToggleDisclosure(node, ctx) {
  const el = document.createElement("button");
  el.type = "button";
  el.className = "hv-node hv-toggle-disclosure";
  el.style.cursor = "pointer";
  el.style.border = "0";
  el.style.background = "transparent";
  el.style.padding = "0";
  el.style.font = "inherit";
  el.style.color = "inherit";
  applyLayoutBox(el, node, ctx, { paddings: false });
  if (node.accessibility?.description) {
    el.setAttribute("aria-label", localizedLabel(node.accessibility.description, ctx.vars));
  }
  stamp(el, node, ctx);
  el.addEventListener("click", (event) => {
    event.stopPropagation();
    const target = document.getElementById(node.target);
    if (!target) return;
    const collapsed = target.style.display === "none";
    target.style.display = collapsed ? "" : "none";
    target.dataset.disclosure = collapsed ? "expanded" : "collapsed";
  });
  for (const [index, child] of (node.items || []).entries()) {
    const childCtx = { ...ctx, path: `${ctx.path}.${index}` };
    const rendered = renderNode(child, childCtx);
    if (rendered) el.append(rendered);
  }
  return el;
}

/** Resolve LocalizationDictionary keys (or legacy {module,key}) then interpolate vars. */
function localizedLabel(value, vars) {
  if (value == null) return "";
  const resolved = (isLocalizedTextRef(value) || isDictionaryKey(value))
    ? resolveLocalizedText(value)
    : value;
  return interpolate(typeof resolved === "string" ? resolved : "", vars);
}

function renderText(node, ctx) {
  const text = localizedLabel(node.text ?? node.appearance?.text ?? "", ctx.vars);
  const el = document.createElement("span");
  el.className = "hv-node hv-text";
  el.textContent = text;
  applyFont(el, node.font, ctx.vars);
  const contentColor = node.color ?? node.text_color;
  if (contentColor) el.style.color = resolveColor(contentColor, ctx.vars);
  applyTextWrap(el, node.font);
  applyLayoutBox(el, node, ctx, { paddings: false });
  if (node.accessibility?.description) {
    el.title = localizedLabel(node.accessibility.description, ctx.vars);
  }
  stamp(el, node, ctx);
  return el;
}


function containerIsInteractive(action) {
  if (!action || typeof action !== "object") return false;
  return Boolean(action.event || action.show);
}

function resolveContainerAction(node) {
  if (node?.action && typeof node.action === "object") return node.action;
  const show = node?.interaction?.show;
  if (typeof show === "string" && show) return { show };
  return null;
}

function renderContainer(node, ctx) {
  const action = resolveContainerAction(node);
  const interactive = containerIsInteractive(action);
  const el = document.createElement(interactive ? "button" : "div");
  el.className = "hv-node hv-container";
  if (ctx.path === "state" || isWidgetVisualRootPath(ctx.path)) el.classList.add("hv-root");
  el.style.boxSizing = "border-box";
  el.style.border = "0";
  el.style.font = "inherit";
  el.style.color = "inherit";
  el.style.backgroundColor = "transparent";
  el.style.textAlign = "inherit";
  applyDirection(el, node.direction, node, ctx.vars);
  if (!node.direction) {
    el.style.display = "flex";
    el.style.alignItems = "center";
  }
  const layout = resolveLayout(node);
  const gap = resolveBoxSide(layout.gap, ctx.vars);
  if (gap != null) el.style.gap = gap;
  el.style.appearance = "none";
  const skSpec = nodeSkeletonSpec(node);
  const layoutNode = isSkeletonPreviewMode(ctx.previewMode) && skSpec ? nodeWithSkeletonLayout(node) : node;
  applyLayoutBox(el, layoutNode, ctx, { paddings: false });
  if (interactive) {
    el.style.padding = "0";
    el.style.minHeight = "0";
    el.style.minWidth = "0";
    el.style.lineHeight = "normal";
  }
  if (layout.paddings) applyBox(el, layout.paddings, "padding", ctx.vars);
  const hasBackground = Array.isArray(node.background) && node.background.length > 0;
  if (!applySkeletonSurface(el, node, ctx)) {
    if (hasBackground) {
      applyBackground(el, node.background, ctx.vars);
    } else if (node.color) {
      const fill = resolveColor(node.color, ctx.vars);
      if (fill) el.style.backgroundColor = fill;
    }
  }
  const contentColor = node.color ? resolveColor(node.color, ctx.vars) : null;
  if (contentColor) el.style.color = contentColor;
  applyFont(el, node.font, ctx.vars);
  applyBorderRadius(el, borderRadius(node), ctx.vars);
  applySkeletonVisibility(el, node, ctx);
  const stateDescription = node.accessibility?.state_description
    ? localizedLabel(node.accessibility.state_description, ctx.vars)
    : "";
  if (node.accessibility?.description) {
    const description = localizedLabel(node.accessibility.description, ctx.vars);
    el.setAttribute("aria-label", description);
    el.title = description;
  }
  if (stateDescription) {
    el.dataset.state = stateDescription;
    if (stateDescription === "выбрано") el.setAttribute("aria-current", "page");
  }
  if (node.selectedState) el.setAttribute("aria-current", "page");
  if (node.id) el.id = node.id;
  if (node.disclosure?.default_expanded === false) {
    el.style.display = "none";
    el.dataset.disclosure = "collapsed";
  }
  applyInteractionHover(el, node, ctx.vars);
  if (node.id) el.dataset.testid = node.id;
  if (action?.show) {
    el.dataset.hvShow = action.show;
    const token = (ctx.widgetName && ctx.phaseByWidget?.[ctx.widgetName])
      || (ctx.previewMode && ctx.previewMode !== "data" ? ctx.previewMode : null)
      || ctx.data?.phase;
    const activePhase = phaseTokenToId(token) ?? token;
    if (activePhase && (action.show === activePhase || action.show === token)) {
      el.setAttribute("aria-pressed", "true");
    }
  }
  if (interactive) {
    el.style.cursor = "pointer";
    el.type = "button";
    el.addEventListener("click", (event) => {
      event.stopPropagation();
      ctx.onAction?.(action, { el, widgetName: ctx.widgetName });
    });
  }
  if (typeof node.dataset === "string" && node.direction?.type === "horizontal") {
    el.style.flexWrap = "nowrap";
    el.style.overflowX = "auto";
    el.style.overflowY = "hidden";
    el.style.scrollbarWidth = "none";
  }
  stamp(el, node, ctx);
  for (const [index, child] of (node.items || []).entries()) {
    const childCtx = { ...ctx, path: `${ctx.path}.${index}` };
    const rendered = renderNode(child, childCtx);
    if (!rendered) continue;
    if (node.direction?.type === "overlap") {
      rendered.style.gridArea = "1 / 1";
      rendered.style.zIndex = index === 0 ? "2" : "1";
      const placement = child.direction;
      if (placement?.horizontal === "start") rendered.style.justifySelf = "start";
      else if (placement?.horizontal === "end") rendered.style.justifySelf = "end";
      else if (placement?.horizontal === "center") rendered.style.justifySelf = "center";
      if (placement?.vertical === "bottom" || placement?.vertical === "end") rendered.style.alignSelf = "end";
      else if (placement?.vertical === "top" || placement?.vertical === "start") rendered.style.alignSelf = "start";
      else if (placement?.vertical === "center") rendered.style.alignSelf = "center";
    }
    el.append(rendered);
  }
  return el;
}

function stamp(el, node, ctx) {
  const id = ctx.nextId++;
  el.dataset.hvId = String(id);
  if (ctx.path) el.dataset.hvKey = ctx.path;
  ctx.nodes.set(id, { node, el, label: nodeLabel(node), key: ctx.path });
}

function nodeSkeletonSpec(node) {
  const spec = node?.skeleton;
  if (!spec || typeof spec !== "object") return null;
  return spec;
}

/** `null` | `gone` | `plate` | `container` — один порядок для любого типа узла. */
function isSkeletonPreviewMode(mode) {
  return mode === "phase-skeleton" || mode === "skeleton" || mode === "data-skeleton";
}

function skeletonPreviewKind(node, ctx) {
  if (!isSkeletonPreviewMode(ctx.previewMode)) return null;
  const spec = nodeSkeletonSpec(node);
  if (!spec) return null;
  if (spec.visibility === "gone") return "gone";
  if ((node.items?.length ?? 0) > 0) return "container";
  return "plate";
}

function skeletonLayout(node) {
  const layout = { ...(node.layout || {}) };
  const skeleton = node.skeleton || {};
  if (skeleton.width != null) {
    layout.width = { type: "fixed", value: Number(skeleton.width) };
  }
  if (skeleton.height != null) {
    layout.height = { type: "fixed", value: Number(skeleton.height) };
  } else if (layout.height?.type !== "fixed") {
    const lineHeight = node.font?.line_height;
    if (lineHeight != null) layout.height = { type: "fixed", value: lineHeight };
  }
  return layout;
}

function applySkeletonVisibility(el, node, ctx) {
  if (!isSkeletonPreviewMode(ctx.previewMode)) return;
  if (node.skeleton?.visibility === "invisible") {
    el.style.visibility = "hidden";
  }
}

function nodeWithSkeletonLayout(node) {
  if (!nodeSkeletonSpec(node)) return node;
  return { ...node, layout: skeletonLayout(node) };
}

function applySkeletonSurface(el, node, ctx) {
  if (!isSkeletonPreviewMode(ctx.previewMode) || !nodeSkeletonSpec(node)) return false;
  const color = node.skeleton.color ? resolveColor(node.skeleton.color, ctx.vars) : null;
  if (color) el.style.backgroundColor = color;
  return Boolean(color);
}

function renderSkeleton(node, ctx) {
  const el = document.createElement("div");
  el.className = "hv-node hv-skeleton";
  el.style.boxSizing = "border-box";
  el.style.flex = "0 0 auto";
  applyLayoutBox(el, nodeWithSkeletonLayout(node), ctx, { paddings: false });
  applySkeletonSurface(el, node, ctx);
  applyBorderRadius(el, borderRadius(node), ctx.vars);
  applySkeletonVisibility(el, node, ctx);
  stamp(el, node, ctx);
  return el;
}

export function renderResolvedNode(node, ctx) {
  if (!node || typeof node !== "object") {
    const empty = document.createElement("div");
    empty.textContent = "Пустой узел";
    return empty;
  }
  const source = ctx.variantsApplied
    ? node
    : applyActiveVariants(node);
  const paintCtx = ctx.variantsApplied ? ctx : { ...ctx, variantsApplied: true };
  const paintNode = normalizeBlockForPaint(source);
  if (paintNode.type === "toggle_disclosure") {
    return renderToggleDisclosure(paintNode, paintCtx);
  }
  const { node: resolved, visible } = resolveNodeForBreakpoint(paintNode, paintCtx.breakpoint);
  if (!visible) return null;
  const skeletonKind = skeletonPreviewKind(resolved, paintCtx);
  if (skeletonKind === "gone") return null;
  if (skeletonKind === "plate") return renderSkeleton(resolved, paintCtx);
  if (resolved.type === "icon" || resolved.type === "image" || resolved.type === "custom") return renderIcon(resolved, paintCtx);
  if (resolved.type === "text") return renderText(resolved, paintCtx);
  return renderContainer(resolved, paintCtx);
}

export function renderNode(node, ctx) {
  if (!node || typeof node !== "object") {
    return renderResolvedNode(node, ctx);
  }
  if (node.widget) {
    const materialized = materializeWidgetNode(node, ctx);
    if (!materialized) return null;
    const widgetName = resolveWidgetCatalogName(node) ?? node.widget;
    return renderResolvedNode(materialized, { ...ctx, widgetName });
  }
  return renderResolvedNode(node, ctx);
}

export function nodeTitle(node) {
  return nodeLabel(node);
}
